import apiClient from "./apiClient";
import { EntryReportReason, EntryReportStatus } from "./entryReportService";

export type { EntryReportReason, EntryReportStatus };

export interface SubmitMessageReportRequest {
  reason: EntryReportReason;
  otherReason?: string;
}

export interface MessageReportDto {
  id: number;
  messageId: number;
  messageContent: string;
  messageSenderId: number;
  messageSenderUsername: string;
  conversationId: number;
  reporterUserId: number;
  reporterUsername: string;
  reason: EntryReportReason;
  otherReason?: string;
  createdAt: string;
  status: EntryReportStatus;
  resolvedById?: number;
  resolvedByUsername?: string;
  resolvedAt?: string;
  resolutionNote?: string;
}

export interface AdminMessageReportFilters {
  status: string;
  senderUsername: string;
  reporterUsername: string;
  reason: string;
  startDate: string;
  endDate: string;
}

export interface AdminMessageReportsPage {
  content: MessageReportDto[];
  currentPage: number;
  totalPages: number;
  totalItems: number;
}

/**
 * Submit a report for the given message.
 * Throws on 400 (own message / OTHER without text) or 409 (already reported).
 */
export async function submitMessageReport(
  messageId: number,
  data: SubmitMessageReportRequest
): Promise<void> {
  await apiClient.post(`/api/messages/${messageId}/report`, data);
}

/**
 * IDs of the messages the current user has reported inside one conversation.
 *
 * Called once when a conversation is opened. The per-message status endpoint below
 * answers the same question one message at a time, which would mean 30 requests to
 * render a single page of the thread.
 */
export async function getReportedMessageIds(
  conversationId: number
): Promise<number[]> {
  return apiClient.get<number[]>(
    `/api/messages/conversations/${conversationId}/reported`
  );
}

/**
 * Check whether the authenticated user has already reported this message.
 * Used to disable the report button without requiring a failed submit.
 *
 * Currently unused by the app — getReportedMessageIds above covers the whole
 * conversation in one request. Kept because it is a published endpoint.
 */
export async function getMessageReportStatus(
  messageId: number
): Promise<{ reported: boolean }> {
  return apiClient.get<{ reported: boolean }>(
    `/api/messages/${messageId}/report/status`
  );
}

// ── Admin endpoints ───────────────────────────────────────────────────────

export async function getAdminMessageReports(
  filters: AdminMessageReportFilters,
  page: number,
  size: number
): Promise<AdminMessageReportsPage> {
  const params = new URLSearchParams({
    status:           filters.status,
    senderUsername:   filters.senderUsername,
    reporterUsername: filters.reporterUsername,
    reason:           filters.reason,
    startDate:        filters.startDate,
    endDate:          filters.endDate,
    page:             String(page),
    size:             String(size),
  });
  return apiClient.get<AdminMessageReportsPage>(
    `/api/admin/message-reports?${params}`
  );
}

export async function dismissMessageReport(
  reportId: number,
  note?: string
): Promise<MessageReportDto> {
  return apiClient.patch<MessageReportDto>(
    `/api/admin/message-reports/${reportId}/dismiss`,
    { note }
  );
}

export async function actionMessageReport(
  reportId: number,
  note?: string
): Promise<MessageReportDto> {
  return apiClient.patch<MessageReportDto>(
    `/api/admin/message-reports/${reportId}/action`,
    { note }
  );
}